import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import { getMap } from '../api/client';
import NestedEggCanvas from './ListView/NestedEggCanvas';

// Read-only map view for expired-trial users: same renderer as the public
// share view, but loaded through the authenticated map endpoint and with
// an Export PDF button. No editing surfaces at all.
export default function ReadOnlyMapView() {
  const { id: mapId } = useParams();
  const navigate = useNavigate();
  const [map, setMap] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const data = await getMap(mapId);
        setMap(data);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [mapId]);

  function formatDate(d) {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }

  async function handleExport() {
    if (exporting) return;
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const base = window.location.hostname === 'localhost' ? 'http://localhost:3001/api' : '/api';
      const res = await fetch(`${base}/export-pdf/${mapId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${map?.name || 'Clarity Map'} - Report.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export error:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <span className="text-txt-secondary text-sm">Loading...</span>
      </div>
    );
  }

  if (error || !map) {
    return (
      <div className="min-h-screen bg-canvas flex flex-col items-center justify-center px-6">
        <img src="/bcd-icon.png" alt="" className="w-12 opacity-20 mb-4" />
        <p className="text-txt-secondary">This map is no longer available.</p>
      </div>
    );
  }

  return (
    <div className="h-screen bg-canvas flex flex-col overflow-hidden">
      {/* Branded bar */}
      <div className="bg-navy px-6 py-2.5 flex items-center justify-between flex-shrink-0 border-b-[3px] border-gold">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-1.5 text-white/70 hover:text-white text-xs transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Dashboard
          </button>
          <span className="text-white/30">|</span>
          <img src="/bcd-icon.png" alt="BCD" className="h-5 w-auto" />
          <span className="text-white font-heading font-bold text-sm uppercase tracking-wide">
            The Clarity Map
          </span>
          <span className="bg-gold/20 border border-gold/40 text-gold text-[10px] font-heading font-bold uppercase tracking-widest px-2 py-0.5 rounded">
            View only
          </span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-white/70 text-xs">
            <span className="font-semibold">{map.name}</span>
            <span>·</span>
            <span>{map.client?.name}{map.updatedAt ? ` · ${formatDate(map.updatedAt)}` : ''}</span>
          </div>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-1.5 bg-gold text-black h-8 px-3 rounded-lg font-heading font-bold uppercase text-xs hover:bg-gold/90 transition-colors disabled:opacity-50"
          >
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {exporting ? 'Generating...' : 'Export PDF'}
          </button>
        </div>
      </div>

      {/* Nested egg canvas (includes measurement bar) */}
      <NestedEggCanvas tiles={map.tiles} mapName={map.name} measurement={map.measurement} />

      {/* Footer */}
      <div className="flex-shrink-0 px-6 py-2 border-t border-black/15 flex items-center justify-between bg-white">
        <div className="flex items-center gap-1.5">
          <img src="/bcd-icon.png" alt="" className="h-3.5 w-auto opacity-50" />
          <span className="text-[10px] text-txt-secondary uppercase tracking-wider">
            The Clarity Map · Brain-Centric Design
          </span>
        </div>
        <span className="text-[10px] text-txt-secondary uppercase tracking-wider">
          {map.client?.name}{map.updatedAt ? ` · ${formatDate(map.updatedAt)}` : ''}
        </span>
      </div>
    </div>
  );
}
